import React, { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { axios } from '../..';
import { setCookie } from "../../utils/cookies";
import {
  logSsoB2cLogin,
  logSsoB2cLoginFailed,
} from "../../utils/logSsoAccessLog";
import {
  FIRM_LOGIN_NOT_ALLOWED_CODE,
  resolveAuthErrorPayload,
} from "../../utils/authErrorPayload";

enum B2CErrors {
  "AADB2C99002" = "User does not exist. Please link your account before you can sign in.",
}

const GoogleCallback: React.FC = () => {
  const location = useLocation();

  const handleCallbackLogin = useCallback(async () => {
    try {
      const urlParams = new URLSearchParams(location.search);
      const fragment = new URLSearchParams(window.location.hash.substring(1));

      // Check for errors first
      const error = urlParams.get("error") || fragment.get("error");
      const errorDescription =
        urlParams.get("error_description") || fragment.get("error_description");

      if (error) {
        if (errorDescription?.includes(Object.keys(B2CErrors)[0])) {
          void logSsoB2cLoginFailed("google", "account_not_linked");
          sessionStorage.removeItem("googleAuthAction");
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_NOT_LINKED",
              errorId: Object.keys(B2CErrors)[0],
              error: B2CErrors.AADB2C99002,
              data: {
                urlParams: Object.fromEntries(urlParams.entries()),
                fragment: Object.fromEntries(fragment.entries()),
              },
            },
            window.location.origin
          );
          window.close();
          sessionStorage.removeItem("googleAuthAction");
          return;
        } else {
          void logSsoB2cLoginFailed("google", error);
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: error,
              errorDescription: errorDescription,
              fullUrl: window.location.href,
            },
            window.location.origin
          );
          window.close();
          return;
        }
      }

      // Check for B2C tokens (they come in the URL fragment)
      const idToken = fragment.get("id_token");
      const accessToken = fragment.get("access_token");
      // const state = fragment.get("state") || urlParams.get("state");

      if (idToken) {
        // Decode the JWT to get user information and check 2FA
        let decodedToken;
        try {
          // Simple JWT decode (just for getting claims, not verifying signature)
          const base64Url = idToken.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join("")
          );
          decodedToken = JSON.parse(jsonPayload);
        } catch (decodeError) {
          // Error decoding token
          console.error("Error decoding token", decodeError);
        }

        void logSsoB2cLogin("google", idToken);

        // Send the B2C tokens to the parent window
        window.opener?.postMessage(
          {
            type: "GOOGLE_AUTH_SUCCESS",
            action: "login",
            data: {
              status: "success",
              action: "login",
              tokens: {
                id_token: idToken,
                access_token: accessToken,
                token_type: "Bearer",
              },
              user: decodedToken,
              message: "B2C Google SSO successful",
            },
          },
          window.location.origin
        );
        setCookie("ssoLogin", "true", 0);

        // Clean up the stored action after successful processing
        sessionStorage.removeItem("googleAuthAction");
        window.close();
      } else {
        void logSsoB2cLoginFailed("google", "missing_id_token");
        sessionStorage.removeItem("googleAuthAction");
        window.opener?.postMessage(
          {
            type: "GOOGLE_AUTH_ERROR",
            error: "No tokens received from B2C",
            fullUrl: window.location.href,
          },
          window.location.origin
        );
        window.close();
      }
    } catch (error) {
      void logSsoB2cLoginFailed(
        "google",
        (error as Error)?.message || "callback_error",
      );
      // Clean up the stored action
      sessionStorage.removeItem("googleAuthAction");

      // Send error message to parent
      window.opener?.postMessage(
        {
          type: "GOOGLE_AUTH_ERROR",
          error: (error as Error)?.message || "Callback processing failed",
        },
        window.location.origin
      );
      window.close();
    }
  }, [location.search]);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        const action = sessionStorage.getItem("googleAuthAction") || "login";
        const error = urlParams.get("error");

        if (error) {

          sessionStorage.removeItem("googleAuthAction");
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: error,
              errorDescription: urlParams.get("error_description"),
              fullUrl: window.location.href,
            },
            window.location.origin
          );
          window.close();
          return;
        }

        if (!code) {
          sessionStorage.removeItem("googleAuthAction");
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: "No authorization code received",
            },
            window.location.origin
          );
          window.close();
          return;
        }

        // Exchange code for tokens
        const response = await axios.Get(
          `/google-sso?action=callback&code=${code}&state=${state || ""}&googleAuthAction=link`
        );

        if (response.data.status === "account_already_linked") {
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ACCOUNT_ALREADY_LINKED",
              data: response.data,
            },
            window.location.origin
          );
          window.close();
          return;
        }

        if (response.data.status === "success") {
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_SUCCESS",
              action: response.data.action,
              data: response.data,
              debugging: true,
            },
            window.location.origin
          );
          // Clean up the stored action after successful processing
          sessionStorage.removeItem("googleAuthAction");

          // Don't close immediately for linking actions - let the parent handle it
          if (action !== "link") {
            window.close();
          }
        } else if (
          response.data.status === "error" &&
          response.data.action === "retry_required"
        ) {
          sessionStorage.removeItem("googleAuthAction");
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: response.data.message || "Please try again",
              serverResponse: response.data,
            },
            window.location.origin
          );
          window.close();
        } else {
          sessionStorage.removeItem("googleAuthAction");
          window.opener?.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: response.data.message || "Authentication failed",
              serverResponse: response.data,
            },
            window.location.origin
          );
          window.close();
        }
      } catch (error) {
        console.error("Errroooor", error);
        const action = sessionStorage.getItem("googleAuthAction") || "login";

        // Clean up the stored action
        sessionStorage.removeItem("googleAuthAction");

        const axiosData = (error as any)?.response?.data;
        const resolved = resolveAuthErrorPayload(axiosData);
        const message =
          resolved.message ||
          (error as Error)?.message ||
          "Callback processing failed";

        // Send error message to parent
        window.opener?.postMessage(
          {
            type: "GOOGLE_AUTH_ERROR",
            error: message,
            code: resolved.isFirmLoginNotAllowed
              ? FIRM_LOGIN_NOT_ALLOWED_CODE
              : undefined,
          },
          window.location.origin
        );

        // Don't close immediately for linking actions - let the parent handle it
        if (action !== "link") {
          window.close();
        }
      }
    };

    const action = sessionStorage.getItem("googleAuthAction") || "login";
    window.opener?.postMessage(
      {
        type: "GOOGLE_AUTH_ACTION",
        action: action,
      },
      window.location.origin
    );
    if (action === "login") {
      handleCallbackLogin();
    } else if (action === "link") {
      handleCallback();
    }
  }, [location.search, handleCallbackLogin, axios]);

  return <></>;
};

export default GoogleCallback;
